// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// here ever leaves the device; only the small derived-signal slice (computeSignals)
// is sent for an AI recap/ask call (see brain.ts).
//
// Data model — a catalog of OWNED seasonal possessions:
//   item { name, category, season, occasion, location, quantity, photo_ref, last_used_on, notes }

import { invoke } from "./tauri-web";

export interface Item {
  id: string;
  /** Item name, e.g. "warm-white string lights". */
  name: string;
  /** Normalized category token, e.g. "decor". "" when unset. */
  category: string;
  /** Normalized season token, e.g. "christmas". "" when unset. */
  season: string;
  /** Free-text occasion/tag, e.g. "Christmas". "" when unset. */
  occasion: string;
  /** Where it is stored, free text, e.g. "Attic Bin 2". "" when unset. */
  location: string;
  /** How many you own (>=1). */
  quantity: number;
  /** Local photo reference (file path / asset uri). Never uploaded. "" when none. */
  photo_ref: string;
  /** Last used/worn/taken-out date, "YYYY-MM-DD". null = never used. */
  last_used_on: string | null;
  /** Optional free-text note. */
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface NewItem {
  name: string;
  category?: string;
  season?: string;
  occasion?: string;
  location?: string;
  quantity?: number;
  photo_ref?: string;
  last_used_on?: string | null;
  notes?: string;
}

export type ItemPatch = Partial<{
  name: string;
  category: string;
  season: string;
  occasion: string;
  location: string;
  quantity: number;
  photo_ref: string;
  /** Pass null to CLEAR the use-log; omit the key to leave it unchanged. */
  last_used_on: string | null;
  notes: string;
}>;

// ── Derived signals (computed on-device in Rust; only aggregates are sent to the brain) ──
// Mirrors src-tauri/src/signals.rs::StashSignals. Every field is a computed aggregate of
// OWNED possessions — counts, location buckets, last-used dates, duplicate clusters — never
// the bulk catalog. This is the payload the AI recap/ask reasons over (see brain.ts).
export interface Overall {
  total_items: number;
  total_units: number;
  distinct_seasons: number;
  distinct_locations: number;
  distinct_categories: number;
  used_last_cycle: number;
  never_used: number;
  photographed: number;
}
export interface SeasonRollup {
  season: string;
  items: number;
  units: number;
  locations: number;
  used_last_cycle: number;
  never_used: number;
}
export interface LocationRollup {
  location: string;
  items: number;
  units: number;
  seasons: string[];
}
export interface CategoryRollup {
  category: string;
  items: number;
  units: number;
}
export interface DuplicateCluster {
  name: string;
  season: string;
  rows: number;
  total_units: number;
  locations: string[];
}
export interface LocationGroup {
  location: string;
  items: number;
  units: number;
  item_names: string[];
}
export interface FocusItem {
  name: string;
  category: string;
  occasion: string;
  location: string;
  quantity: number;
  last_used_on: string | null;
  used_last_cycle: boolean;
}
export interface SeasonFocus {
  season: string;
  total_items: number;
  total_units: number;
  locations: LocationGroup[];
  never_used_items: string[];
  items: FocusItem[];
}
export type FactTone = "good" | "watch" | "neutral";
/** One pre-ranked, ready-to-say observational fact (computed in Rust; see signals.rs).
 *  `text` is a complete sentence already carrying the figure + unit — purely observational. */
export interface HeadlineFact {
  kind: "count" | "location" | "season" | "duplicate" | "unused" | "spread";
  text: string;
  tone: FactTone;
}
/** A non-obvious cross-signal the on-screen chips can't show — a redundancy (same item split
 * across different bins) or dormant-stock (a season where most items have never been logged as
 * used). The AI recap LEADS with lead_insights[0]; purely observational, never advice. */
export interface CrossSignal {
  kind: string;
  headline: string;
}
export interface StashSignals {
  schema: string;
  generated_for: string;
  overall: Overall;
  by_season: SeasonRollup[];
  by_location: LocationRollup[];
  by_category: CategoryRollup[];
  duplicates: DuplicateCluster[];
  focus: SeasonFocus | null;
  headline_facts: HeadlineFact[];
  lead_insights: CrossSignal[];
}

export interface Facets {
  seasons: string[];
  locations: string[];
  categories: string[];
}

export const store = {
  listItems: (opts?: { season?: string; location?: string; category?: string; limit?: number }) =>
    invoke<Item[]>("list_items", {
      season: opts?.season,
      location: opts?.location,
      category: opts?.category,
      limit: opts?.limit ?? 50000,
    }),
  getItem: (id: string) => invoke<Item | null>("get_item", { id }),
  createItem: (input: NewItem) => invoke<Item>("create_item", { input }),
  updateItem: (id: string, patch: ItemPatch) => invoke<void>("update_item", { id, patch }),
  deleteItem: (id: string) => invoke<void>("delete_item", { id }),
  /** Stamp an item as used today. `date` = client local YYYY-MM-DD. */
  markUsed: (id: string, date: string) => invoke<void>("mark_used", { id, date }),
  /** Compute the derived-signals object on-device. `focusSeason` scopes the focus block. */
  computeSignals: (today: string, focusSeason?: string) =>
    invoke<StashSignals>("compute_signals", { today, focusSeason: focusSeason ?? null }),
  /** Distinct seasons / locations / categories present — drives the filter chips. */
  listFacets: () => invoke<Facets>("list_facets"),
};

// ── Pure policy / presentation helpers ─────────────────────────────────────────

/** Curated season suggestions; user can also type their own. Values are the normalized token. */
export const DEFAULT_SEASONS = [
  "christmas", "winter", "spring", "summer", "autumn",
  "halloween", "thanksgiving", "easter", "camping", "beach", "ski", "rainy",
] as const;

/** Curated category suggestions. */
export const DEFAULT_CATEGORIES = [
  "decor", "clothing", "footwear", "gear", "lights",
  "kitchen", "bedding", "tools", "toys", "sports",
] as const;

/** Curated storage-location suggestions (free text; these are just quick picks). */
export const DEFAULT_LOCATIONS = [
  "Attic", "Garage", "Basement", "Hall closet", "Bedroom closet",
  "Storage unit", "Under bed", "Shed",
] as const;

/** Title-case a normalized token for display ("christmas" → "Christmas"). */
export function titleCase(token: string): string {
  if (!token) return "";
  return token
    .split(/[\s-]+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/** A warm emoji for a season token (display only). */
const SEASON_EMOJI: Record<string, string> = {
  christmas: "🎄", winter: "❄️", spring: "🌷", summer: "☀️", autumn: "🍂", fall: "🍂",
  halloween: "🎃", thanksgiving: "🦃", easter: "🐣", camping: "⛺", beach: "🏖️", ski: "🎿", rainy: "🌧️",
};
export function seasonEmoji(season: string): string {
  return SEASON_EMOJI[season.trim().toLowerCase()] ?? "📦";
}

/** Normalize a token — lowercase, trimmed, collapse inner whitespace to single space. */
export function normalizeToken(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

const toLocalDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** "last used 8 months ago" / "never used" — a plain fact, never a nudge. */
export function lastUsedCopy(lastUsedOn: string | null, today = new Date()): string {
  if (!lastUsedOn) return "Never used";
  const d = toLocalDate(lastUsedOn);
  if (!d) return "Never used";
  const days = Math.floor((today.getTime() - d.getTime()) / 86_400_000);
  if (days < 0) return `Used ${lastUsedOn}`;
  if (days === 0) return "Used today";
  if (days === 1) return "Used yesterday";
  if (days < 30) return `Used ${days} days ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `Used ${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.round(days / 365);
  return `Used ${years} year${years === 1 ? "" : "s"} ago`;
}

export interface CatalogSummary {
  totalItems: number;
  totalUnits: number;
  seasons: number;
  locations: number;
  neverUsed: number;
}

/** Quick on-device summary for the hero (no AI). */
export function summarize(items: Item[]): CatalogSummary {
  const seasons = new Set<string>();
  const locations = new Set<string>();
  let units = 0;
  let neverUsed = 0;
  for (const i of items) {
    // Defensive: rows written by older builds can carry undefined where "" is expected
    // (the file-a-box save crashed on exactly this, measured live 2026-08-19).
    const season = (i.season || "").trim();
    const location = (i.location || "").trim();
    if (season) seasons.add(season.toLowerCase());
    locations.add(location ? location.toLowerCase() : "(no location)");
    units += i.quantity ?? 1;
    if (!i.last_used_on) neverUsed += 1;
  }
  return { totalItems: items.length, totalUnits: units, seasons: seasons.size, locations: locations.size, neverUsed };
}

export function summaryCopy(s: CatalogSummary): string {
  if (s.totalItems === 0) return "Catalog your first seasonal item — it takes a few seconds.";
  const unitNote = s.totalUnits !== s.totalItems ? ` (${s.totalUnits} total units)` : "";
  return `${s.totalItems} item${s.totalItems === 1 ? "" : "s"}${unitNote} across ${s.seasons || 1} season${s.seasons === 1 ? "" : "s"} and ${s.locations} location${s.locations === 1 ? "" : "s"}`;
}
